import React from "react";
import DataTable from "react-data-table-component";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "./adminApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RefreshCcw, ShieldCheck, Plus, Edit, CheckCircle, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { SIDEBAR_CONFIG, FOOTER_CONFIG } from "@/config/menuConfig";

const roleSchema = z.object({
  name: z.string().min(2, "Role name must be at least 2 characters"),
  description: z.string().optional(),
  permissions: z.array(z.string()).default([]),
  default_landing_page: z.string().optional().nullable(),
  allow_unassigned_leads: z.boolean().default(false),
});

type RoleFormValues = z.infer<typeof roleSchema>;

interface Role {
  id: number;
  name: string;
  description: string | null;
  permissions: string[];
  default_landing_page: string | null;
  allow_unassigned_leads: boolean;
}

const RoleManagement = () => {
  const [filterText, setFilterText] = React.useState("");
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingRole, setEditingRole] = React.useState<Role | null>(null);
  
  const allMenuItems = React.useMemo(() => {
    const items: { id: string, label: string }[] = [];
    [...SIDEBAR_CONFIG, ...FOOTER_CONFIG].forEach(category => {
      category.items.forEach(item => {
        if (!item.hideFromMenu || item.id === "Admin Login") {
          items.push({ id: item.id, label: item.label });
        }
      });
    });
    // Add custom views that are not in the sidebar/footer config but are handled in AdminDashboard
    items.push({ id: "users", label: "User Management" });
    items.push({ id: "roles", label: "Role Management" });
    return items;
  }, []);

  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-roles"],
    queryFn: async () => {
      const response = await adminService.getRoles();
      return response.data as Role[];
    },
  });

  const createMutation = useMutation({
    mutationFn: (roleData: RoleFormValues) => adminService.createRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      // Also invalidate reference data as roles might have changed
      queryClient.invalidateQueries({ queryKey: ["admin-reference-data"] });
      toast.success("Role created successfully");
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to create role");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (roleData: RoleFormValues & { id: number }) => adminService.updateRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-reference-data"] });
      toast.success("Role updated successfully");
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to update role");
    },
  });

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
      default_landing_page: null,
      allow_unassigned_leads: false,
    },
  });

  React.useEffect(() => {
    if (editingRole) {
      form.reset({
        name: editingRole.name,
        description: editingRole.description || "",
        permissions: editingRole.permissions || [],
        default_landing_page: editingRole.default_landing_page,
        allow_unassigned_leads: editingRole.allow_unassigned_leads ?? false,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        permissions: [],
        default_landing_page: null,
        allow_unassigned_leads: false,
      });
    }
  }, [editingRole, form]);

  const onSubmit = (values: RoleFormValues) => {
    const formattedValues = {
      ...values,
      default_landing_page: values.default_landing_page === "none" ? null : values.default_landing_page,
    };
    if (editingRole) {
      updateMutation.mutate({ ...formattedValues, id: editingRole.id });
    } else {
      createMutation.mutate(formattedValues);
    }
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRole(null);
    setIsModalOpen(true);
  };

  const filteredItems = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(
      (item) =>
        (item.name && item.name.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.description && item.description.toLowerCase().includes(filterText.toLowerCase()))
    );
  }, [data, filterText]);

  const columns = [
    {
      name: "ID",
      selector: (row: Role) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Role Name",
      selector: (row: Role) => row.name,
      sortable: true,
      width: "200px",
    },
    {
      name: "Description",
      selector: (row: Role) => row.description || "-",
      sortable: true,
    },
    {
      name: "Default Page",
      selector: (row: Role) => {
        if (!row.default_landing_page) return "Dashboard";
        const item = allMenuItems.find(i => i.id === row.default_landing_page);
        return item ? item.label : row.default_landing_page;
      },
      sortable: true,
      width: "180px",
    },
    {
      name: "Actions",
      cell: (row: Role) => (
        <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
          <Edit className="h-4 w-4" />
        </Button>
      ),
      width: "80px",
    },
  ];

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f9fafb',
        borderTopStyle: 'solid' as any,
        borderTopWidth: '1px',
        borderTopColor: '#e5e7eb',
      },
    },
    headCells: {
      style: {
        fontWeight: '600',
        color: '#374151',
        fontSize: '0.875rem',
      },
    },
    cells: {
      style: {
        fontSize: '0.875rem',
        color: '#4b5563',
      },
    },
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-full rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-intellend-green" />
              Role Management
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Define and manage functional roles for the platform.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Filter roles..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="flex items-center gap-2"
            >
              <RefreshCcw className="h-4 w-4" /> Refresh
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              className="flex items-center gap-2 bg-intellend-green hover:bg-intellend-green/90"
            >
              <Plus className="h-4 w-4" /> Add Role
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <DataTable
            columns={columns as any}
            data={filteredItems || []}
            pagination
            progressPending={isLoading}
            progressComponent={<div className="py-20 text-center text-sm text-gray-500">Loading roles...</div>}
            noDataComponent={<div className="py-20 text-center text-sm text-gray-500">No roles found.</div>}
            customStyles={customStyles}
            highlightOnHover
            pointerOnHover
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
          />
        </div>
        {isError && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 text-red-600 text-sm border border-red-100">
            Unable to load role list. Please try again later.
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingRole ? "Edit Role" : "Add New Role"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-1">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Sales Agent" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Brief description of role responsibilities" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="default_landing_page"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Landing Page</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value || undefined}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select default landing page" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None (Default Dashboard)</SelectItem>
                        {allMenuItems.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-[0.7rem] text-gray-500">
                      The page that opens automatically when a user with this role logs in or reloads.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* ── Lead Access Options ── */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">Lead Access Options</h3>
                <div className="rounded-md border p-4 space-y-3">
                  <FormField
                    control={form.control}
                    name="allow_unassigned_leads"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-normal cursor-pointer">
                            Allow leads not assigned to user
                          </FormLabel>
                          <FormDescription className="text-[0.7rem] text-gray-500">
                            Users with this role can see all leads within their BAP scope, not just leads assigned to them.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium">Menu Permissions</h3>
                <div className="rounded-md border p-4 space-y-4 max-h-[300px] overflow-y-auto">
                  {SIDEBAR_CONFIG.map((category) => (
                    <div key={category.title} className="space-y-2">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{category.title}</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {category.items.map((item) => (
                          <FormField
                            key={item.id}
                            control={form.control}
                            name="permissions"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(item.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value, item.id])
                                        : field.onChange(
                                            field.value?.filter((value) => value !== item.id)
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal cursor-pointer">
                                  {item.label}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  {FOOTER_CONFIG.map((category) => (
                    <div key={category.title} className="space-y-2 pt-2 border-t">
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{category.title}</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {category.items.map((item) => (
                          <FormField
                            key={item.id}
                            control={form.control}
                            name="permissions"
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(item.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value, item.id])
                                        : field.onChange(
                                            field.value?.filter((value) => value !== item.id)
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal cursor-pointer">
                                  {item.label}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
              <DialogFooter className="pt-4 border-t">
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingRole ? "Update Role" : "Create Role"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;
